import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

// Mirrors the backend's own fallback (AnnouncementService#resolveStyle:
// `Optional.ofNullable(fontSize).orElse(18)`), used there both for marks with
// no explicit fontSize and for measuring a paragraph with no runs at all —
// must match exactly so a blank line's height agrees between the two.
export const ANNOUNCEMENT_DEFAULT_FONT_SIZE = '18px';

export interface AnnouncementDefaultFontSizeOptions {
  getDefaultFontSize: () => string;
}

// Guarantees every text run always carries an explicit textStyle.fontSize,
// even when the user never touched the font-size control. Without this, a
// plain-typed run has no textStyle mark at all, so it can't get a font-size
// style — which the canvas relies on to zero out the paragraph's own
// (otherwise mismatched) line-height/font-size "strut".
export const AnnouncementDefaultFontSize =
  Extension.create<AnnouncementDefaultFontSizeOptions>({
    name: 'announcementDefaultFontSize',

    addOptions() {
      return {
        getDefaultFontSize: () => ANNOUNCEMENT_DEFAULT_FONT_SIZE,
      };
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey('announcementDefaultFontSize'),
          appendTransaction: (transactions, _oldState, newState) => {
            if (!transactions.some((transaction) => transaction.docChanged)) {
              return null;
            }

            const textStyleType = newState.schema.marks.textStyle;
            if (!textStyleType) {
              return null;
            }

            const defaultFontSize = this.options.getDefaultFontSize();
            const { tr } = newState;
            let modified = false;
            let hasText = false;

            newState.doc.descendants((node, pos) => {
              if (!node.isText || !node.text) {
                return;
              }
              hasText = true;

              const existingMark = textStyleType.isInSet(node.marks);
              if (existingMark?.attrs.fontSize) {
                return;
              }

              const attrs = {
                ...existingMark?.attrs,
                fontSize: defaultFontSize,
              };
              tr.addMark(pos, pos + node.nodeSize, textStyleType.create(attrs));
              modified = true;
            });

            if (!hasText) {
              const storedMarks =
                tr.storedMarks ??
                newState.storedMarks ??
                newState.selection.$from.marks();
              const existingStoredMark = textStyleType.isInSet(storedMarks);
              if (!existingStoredMark?.attrs.fontSize) {
                const attrs = {
                  ...existingStoredMark?.attrs,
                  fontSize: defaultFontSize,
                };
                tr.setStoredMarks([
                  ...storedMarks.filter((mark) => mark.type !== textStyleType),
                  textStyleType.create(attrs),
                ]);
                modified = true;
              }
            }

            return modified ? tr : null;
          },
        }),
      ];
    },
  });
